"use client";

import { useMemo } from "react";
import { CookieGatedMap } from "@/components/cookie-consent/CookieGatedMap";
import { useCookieServiceAllowed } from "@/hooks/useCookieServiceAllowed";

interface HeritageSiteEmbedMapProps {
    /** Modal açıkken true; kapalıyken iframe oluşturulmaz (sayfa yüküne etki yok). */
    visible: boolean;
    latitude?: number | null;
    longitude?: number | null;
    /** Koordinat yoksa harita sorgusu (tam adres metni). */
    addressQuery?: string | null;
    /** Google embed `hl` (örn. tr, en). */
    mapLocale?: string;
    title: string;
}

function normalizeCoord(v: unknown): number | null {
    if (v == null || v === "") return null;
    const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
    if (!Number.isFinite(n)) return null;
    return n;
}

/**
 * Google Maps embed iframe — functional cookie (google_maps) onayı + visible iken yüklenir.
 */
export default function HeritageSiteEmbedMap({
    visible,
    latitude,
    longitude,
    addressQuery,
    mapLocale = "en",
    title,
}: HeritageSiteEmbedMapProps) {
    const { allowed } = useCookieServiceAllowed("google_maps");

    const src = useMemo(() => {
        if (!visible || !allowed) return "";
        const hl = mapLocale === "tr" ? "tr" : "en";
        const lat = normalizeCoord(latitude);
        const lng = normalizeCoord(longitude);
        if (lat != null && lng != null && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
            return `https://www.google.com/maps?q=${lat},${lng}&z=15&output=embed&hl=${encodeURIComponent(hl)}`;
        }
        const q = addressQuery?.trim();
        if (q) {
            return `https://www.google.com/maps?q=${encodeURIComponent(q)}&z=14&output=embed&hl=${encodeURIComponent(hl)}`;
        }
        return "";
    }, [visible, allowed, latitude, longitude, addressQuery, mapLocale]);

    if (!visible) return null;

    return (
        <CookieGatedMap minHeight="200px">
            {src ? (
                <div className="w-full overflow-hidden rounded-xl border border-gray-200 bg-gray-50 shadow-inner">
                    <iframe
                        title={title}
                        src={src}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        className="h-[min(240px,42vh)] w-full min-h-[200px] border-0"
                        allowFullScreen
                    />
                </div>
            ) : null}
        </CookieGatedMap>
    );
}
